import { Fragment, useState, useEffect, useCallback, useRef } from "react";

import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ButtonDefault from "components/Buttons/ButtonDefault";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import ButtonDelete from "components/Buttons/ButtonDelete";
import SpinnerCentered from "components/Loader/SpinnerCentered";

import { httpInviteUser } from "~/services/http/companies";
import { getDriver, saveDriver, deleteDriver } from "services/http/drivers";
import DriverForm from "components/DriverForm";
import AlertDialog from "components/AlertDialog";
import { validateCompany } from "services/companies";
import { validateDriverSubsetForSubmit } from "~/validation/contactFields";

const useStyles = makeStyles((theme) => ({
  content: { padding: theme.spacing(6) },
  title: { paddingBottom: theme.spacing(4) },
}));

const DriverEdit = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const { driverId } = useParams();
  const { myCompany, myUser } = props;

  const [values, setValues] = useState({
    // phone: '+358',
    status: 1,
  });
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const inviteInFlight = useRef(false);

  const handleValidateCompany = useCallback(() => {
    if (validateCompany(myCompany) === false) {
      setAlertOpen(true);
    }
  }, [myCompany]);

  const handleGetDriver = useCallback(() => {
    setLoading(true);
    const request = {
      params: { drivers: true, company: myCompany?.id },
      data: { id: driverId },
    };

    getDriver(request).then((response) => {
      setValues(response);
      setLoading(false);
    });
  }, [driverId, myCompany?.id]);

  useEffect(() => {
    handleValidateCompany();
  }, [handleValidateCompany]);

  useEffect(() => {
    if (driverId && driverId >= -1) {
      handleGetDriver();
    }
  }, [driverId, handleGetDriver]);

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({ ...prev, ...updated_values }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    try {
      validateDriverSubsetForSubmit(values, {});
    } catch (e) {
      enqueueSnackbar(e?.message ?? t("Tarkista punaisella merkatut kentät"), {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        autoHideDuration: 4000,
      });
      return;
    }

    if (values.id) {
      handleSaveUser();
    } else {
      handleInviteUser();
    }
  };

  const handleSaveUser = () => {
    setSaving(true);
    let data = values;
    if (data.id == null) {
      data.username = data.email.toLowerCase();
      data.password =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    }

    // Dont PATCH this existing data:

    if (data.profile_image) {
      delete data.profile_image;
    }

    if (data.verified) {
      delete data.verified;
    }
    // END

    let driver_licence = values.driver_licence;
    if (typeof driver_licence == "string") {
      driver_licence = driver_licence.replace(/\.[0-9]{3}/, "");
    }

    const request = {
      params: { drivers: true, company: myCompany?.id },
      data: {
        ...values,
        driver_licence: driver_licence ? driver_licence : undefined,
      },
    };

    saveDriver(request).then(
      (response) => {
        setValues(response);
        enqueueSnackbar("Tallennettu", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        setErrors(null);
        setSaving(false);
      },
      (errors) => {
        enqueueSnackbar("Tallennus epäonnistui", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });

        if (errors?.status == 500) {
          setErrors({ 500: "Palvelin virhe" });
        } else if (errors?.data) {
          setErrors(errors?.data);
        } else {
          setErrors({ 500: "Tuntematon virhe" });
        }
        setSaving(false);
      },
    );
  };

  const handleInviteUser = () => {
    if (inviteInFlight.current) {
      return;
    }
    inviteInFlight.current = true;
    setSaving(true);
    const request = { data: { ...values, email: values.email.toLowerCase() } };
    httpInviteUser(request).then(
      (response) => {
        if (response?.Successful === "False") {
          inviteInFlight.current = false;
          const errorMsg =
            "Sähköpostin lähetys epäonnistui. Yritä myöhemmin uudelleen.";
          enqueueSnackbar(errorMsg, {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
          setErrors({ 400: errorMsg });
          setSaving(false);
          return;
        }
        enqueueSnackbar("Kutsu lähetetty", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        setSaving(false);
        navigate("/dashboard/drivers");
      },
      (errors) => {
        inviteInFlight.current = false;
        let errorMsg = "Tuntematon virhe";

        if (errors?.data?.errorCode === "ADMIN_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo muita rooleja. Roolin vaihtamiseksi ota yhteyttä tukeen support@opentaxi.fi.";
        } else if (errors?.data?.errorCode === "DRIVER_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo kuljettajarooli. Roolin vaihtamiseksi ota yhteyttä tukeen support@opentaxi.fi.";
        } else if (errors?.data?.errorCode === "CUSTOMER_EXISTS") {
          errorMsg =
            "Kutsua ei voi lähettää. Sähköpostiosoitteella on jo asiakastunnus. Käytä toista sähköpostiosoitetta tai pyydä käyttäjää poistamaan tilinsä palvelusta (mobiilisovellus -> omat tiedot -> Poista profiili)";
        } else if (errors?.data?.errorCode === "EMAIL_SEND_FAILED") {
          errorMsg =
            "Sähköpostin lähetys epäonnistui. Yritä myöhemmin uudelleen.";
        } else if (errors?.data?.errorCode === "INVITE_ALREADY_PENDING") {
          errorMsg =
            "Kutsu tälle osoitteelle on jo olemassa. Yritä lähettää se uudelleen.";
        } else if (errors?.data?.detail) {
          errorMsg = errors.data.detail;
        }

        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });

        setErrors({ 400: errorMsg });
        setSaving(false);
      },
    );
  };

  const handleCancel = () => {
    navigate("/dashboard/drivers");
  };

  const handleDelete = (confirmed) => {
    console.log("handleDelete");
    if (!confirmed) {
      setConfirmOpen(true);
    } else {
      console.log("httpDelete");
      deleteDriver(values?.id, myCompany?.id).then(
        () => {
          enqueueSnackbar("Poistettu", {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
          navigate("/dashboard/drivers/list");
        },
        (_errors) => {
          enqueueSnackbar("Ei voi poistaa.", {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
        },
      );
    }
  };

  return (
    <Paper className={classes.content} elevation={0} square>
      {loading ? (
        <SpinnerCentered />
      ) : (
        <Fragment>
          <Grid
            container
            className={classes.title}
            spacing={3}
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Grid size={12}>
              {values.id && values.id > -1 ? (
                <Typography variant="h4">{t("Muokkaa kuljettajaa")}</Typography>
              ) : (
                <Typography variant="h4">
                  {t("Lisää uusi kuljettaja")}
                </Typography>
              )}
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: "32px" }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <DriverForm values={values} updateValues={updateValues} />
                </Grid>
              </Grid>
            </Box>

            {errors ? (
              <Fragment>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  {t("Tallennus epäonnistui")}
                </Typography>
                <Box>
                  {Object.keys(errors).map((key) => (
                    <Box key={key}>
                      <Typography variant="body" color="error" gutterBottom>
                        {errors[key]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Fragment>
            ) : null}

            <Grid size={{ xs: 12 }}>
              <Grid
                container
                direction="row"
                sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
              >
                <Grid size={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: "flex-start", alignItems: "center" }}
                  >
                    <Grid size={12}>
                      <ButtonDefault onClick={handleCancel}>
                        {t("Peruuta")}
                      </ButtonDefault>
                    </Grid>
                    {myUser?.status == 100 && values?.id && values?.id >= 0 ? (
                      <Grid size={12}>
                        <ButtonDelete onClick={() => handleDelete(false)}>
                          {t("Poista kuljettaja")}
                        </ButtonDelete>
                      </Grid>
                    ) : null}
                  </Grid>
                </Grid>

                {myUser?.status == 100 || myUser?.id == values.id ? (
                  <Grid size={12}>
                    {values.id && values.id > -1 ? (
                      <ButtonPrimary type="submit">
                        {t("Tallenna")}
                      </ButtonPrimary>
                    ) : (
                      <Fragment>
                        {myUser?.status == 100 ? (
                          <ButtonPrimary loading={saving} type="submit">
                            {t("Lähetä kutsu")}
                          </ButtonPrimary>
                        ) : null}
                      </Fragment>
                    )}
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </form>
        </Fragment>
      )}

      <AlertDialog
        titleText={t("Huom!")}
        contentText={t(
          "Sinun täytyy lisätä yrityksesi maksutiedot ja hinnasto, jotta voit lisätä kuljettajia ja autoja.",
        )}
        open={alertOpen}
        setOpen={setAlertOpen}
        showCancel={false}
        confirmText={t("OK")}
        handleConfirm={() => navigate("/dashboard/start")}
        isModal={true}
      />

      <AlertDialog
        titleText={t("Vahvista poisto")}
        contentText={t("Kuljettaja ja sen tiedot poistetaan.")}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        cancelText={t("Peruuta")}
        confirmText={t("Poista")}
        handleConfirm={() => handleDelete(true)}
      />
    </Paper>
  );
};

export default DriverEdit;
